import React, { useState, useEffect, useMemo } from 'react';
import { Truck, Warehouse, Paperclip, Camera, AlertCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { feedService } from '@/services/feedService';
import { arrivalService } from '@/services/arrivalService';
import { cycleService } from '@/services/cycleService';
import { farmService } from '@/services/farmService';
import { Combobox } from '@/components/common/Combobox';
import { formatCurrency } from '@/lib/utils';
import {
  FeedProductRecord,
  SupplierRecord,
  FarmRecord,
  ProductionCycleRecord,
} from '@/types/database';

interface FeedDeliveryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (receipt: any) => void;
  cycleId?: string | null;
  cycleCode?: string;
  farmId?: string | null;
}

export const FeedDeliveryModal: React.FC<FeedDeliveryModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  cycleId,
  cycleCode,
  farmId,
}) => {
  const [isDirectToCycle, setIsDirectToCycle] = useState<boolean>(!!cycleId);
  const [selectedCycleId, setSelectedCycleId] = useState<string>(cycleId || '');
  const [selectedFarmId, setSelectedFarmId] = useState<string>(farmId || '');
  const [selectedSupplierId, setSelectedSupplierId] = useState<string>('');
  const [selectedProductId, setSelectedProductId] = useState<string>('');

  const [receiptDate, setReceiptDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [quantityBags, setQuantityBags] = useState<number | ''>(50);
  const [totalWeightKg, setTotalWeightKg] = useState<number | ''>('');
  const [unitPrice, setUnitPrice] = useState<number | ''>('');
  const [feedMarkupAmount, setFeedMarkupAmount] = useState<number | ''>('');
  const [truckingCost, setTruckingCost] = useState<number | ''>('');
  const [deliveryReceiptNo, setDeliveryReceiptNo] = useState<string>('');
  const [invoiceNo, setInvoiceNo] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [attachedFile, setAttachedFile] = useState<File | null>(null);

  const [products, setProducts] = useState<FeedProductRecord[]>([]);
  const [suppliers, setSuppliers] = useState<SupplierRecord[]>([]);
  const [farms, setFarms] = useState<FarmRecord[]>([]);
  const [cycles, setCycles] = useState<ProductionCycleRecord[]>([]);

  const [loading, setLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Selected product metadata
  const selectedProduct = products.find((p) => p.id === selectedProductId);

  useEffect(() => {
    if (cycleId) {
      setIsDirectToCycle(true);
      setSelectedCycleId(cycleId);
    }
    if (farmId) {
      setSelectedFarmId(farmId);
    }
  }, [cycleId, farmId]);

  useEffect(() => {
    if (!isOpen) return;
    setErrorMsg(null);

    Promise.all([
      feedService.getFeedProducts(),
      arrivalService.getSuppliers(),
      farmService.getActiveFarms(),
      cycleService.getCycles({ status: 'active' }),
    ]).then(([prods, supps, fms, cycs]) => {
      setProducts(prods);
      setSuppliers(supps);
      setFarms(fms);
      setCycles(cycs);

      if (prods.length > 0 && !selectedProductId) {
        setSelectedProductId(prods[0].id);
        if (prods[0].default_unit_price) {
          setUnitPrice(Number(prods[0].default_unit_price));
        }
      }
      if (supps.length > 0 && !selectedSupplierId) {
        setSelectedSupplierId(supps[0].id);
      }
      if (fms.length > 0 && !selectedFarmId && !farmId) {
        setSelectedFarmId(fms[0].id);
      }
      if (cycs.length > 0 && !selectedCycleId && !cycleId) {
        setSelectedCycleId(cycs[0].id);
      }
    });
  }, [isOpen]);

  const handleProductChange = (prodId: string) => {
    setSelectedProductId(prodId);
    const prod = products.find((p) => p.id === prodId);
    if (prod) {
      if (prod.default_unit_price) {
        setUnitPrice(Number(prod.default_unit_price));
      }
      if (quantityBags && prod.unit_weight_kg) {
        setTotalWeightKg(Number(quantityBags) * Number(prod.unit_weight_kg));
      }
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setAttachedFile(e.target.files[0]);
    }
  };

  const subtotalFeedCost = (Number(quantityBags) || 0) * (Number(unitPrice) || 0);
  const totalCalculatedCost =
    subtotalFeedCost + (Number(feedMarkupAmount) || 0) + (Number(truckingCost) || 0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);

    if (!selectedSupplierId) {
      setErrorMsg('Please select a supplier.');
      return;
    }
    if (!selectedProductId) {
      setErrorMsg('Please select a feed product.');
      return;
    }
    if (isDirectToCycle && !selectedCycleId) {
      setErrorMsg('Please select a destination production cycle.');
      return;
    }
    if (!isDirectToCycle && !selectedFarmId) {
      setErrorMsg('Please select a farm location bodega.');
      return;
    }
    if (!quantityBags || Number(quantityBags) <= 0) {
      setErrorMsg('Please enter a valid quantity of bags.');
      return;
    }

    setLoading(true);
    try {
      const derivedWeightKg =
        totalWeightKg !== ''
          ? Number(totalWeightKg)
          : selectedProduct?.unit_weight_kg
          ? Number(quantityBags) * Number(selectedProduct.unit_weight_kg)
          : undefined;

      const res = await feedService.createFeedReceipt(
        {
          supplier_id: selectedSupplierId,
          farm_id: isDirectToCycle ? undefined : selectedFarmId,
          cycle_id: isDirectToCycle ? selectedCycleId : null,
          product_id: selectedProductId,
          receipt_date: receiptDate,
          quantity_bags: Number(quantityBags),
          total_weight_kg: derivedWeightKg,
          unit_price: Number(unitPrice) || 0,
          feed_markup_amount: Number(feedMarkupAmount) || 0,
          trucking_cost: Number(truckingCost) || 0,
          delivery_receipt_no: deliveryReceiptNo.trim() || null,
          invoice_no: invoiceNo.trim() || null,
          is_direct_to_cycle: isDirectToCycle,
          notes: notes.trim() || null,
        },
        attachedFile || undefined
      );

      onSuccess(res);
      onClose();
    } catch (err: any) {
      console.error('Error creating feed delivery receipt:', err);
      setErrorMsg(err.message || 'Failed to save feed delivery receipt.');
    } finally {
      setLoading(false);
    }
  };

  const supplierOptions = useMemo(() => {
    return suppliers.map((s) => ({ value: s.id, label: s.supplier_name }));
  }, [suppliers]);

  const productOptions = useMemo(() => {
    return products.map((p) => ({
      value: p.id,
      label: `${p.product_name} ${p.unit_weight_kg ? `(${p.unit_weight_kg}kg)` : ''}`,
    }));
  }, [products]);

  const farmOptions = useMemo(() => {
    return farms.map((f) => ({ value: f.id, label: f.farm_name }));
  }, [farms]);

  const cycleOptions = useMemo(() => {
    return cycles.map((c) => ({
      value: c.id,
      label: `${c.cycle_code} (${c.growers?.full_name || 'Company-Owned'})`,
    }));
  }, [cycles]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-xl max-h-[92vh] flex flex-col p-4 sm:p-6 overflow-hidden">
        <DialogHeader className="pb-3 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-amber-100 text-amber-800 flex items-center justify-center">
              <Truck className="w-4 h-4" />
            </div>
            <div>
              <DialogTitle className="text-base sm:text-lg font-bold text-slate-900">
                Record Feed Delivery Receipt
              </DialogTitle>
              <p className="text-xs text-slate-500">
                {isDirectToCycle
                  ? 'Supplier delivery allocated directly to a production cycle.'
                  : 'Supplier delivery received into farm bodega warehouse inventory.'}
              </p>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto py-3 space-y-4 no-scrollbar">
          {errorMsg && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-xs text-red-700 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{errorMsg}</span>
            </div>
          )}

          {/* Delivery Route Selection: Bodega vs Direct */}
          <div className="space-y-1.5">
            <label className="text-xs font-bold uppercase tracking-wider text-slate-700">
              Delivery Operating Route *
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setIsDirectToCycle(false)}
                className={`flex items-center gap-2 p-2.5 rounded-lg border text-xs font-semibold text-left transition-all ${
                  !isDirectToCycle
                    ? 'border-amber-600 bg-amber-50/70 text-amber-950 font-bold ring-1 ring-amber-600'
                    : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                }`}
              >
                <Warehouse className="w-4 h-4 text-amber-700" />
                <div>
                  <p className="leading-none">Farm Bodega</p>
                  <p className="text-[10px] text-slate-500 font-normal mt-0.5">Warehouse Storage</p>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setIsDirectToCycle(true)}
                className={`flex items-center gap-2 p-2.5 rounded-lg border text-xs font-semibold text-left transition-all ${
                  isDirectToCycle
                    ? 'border-emerald-600 bg-emerald-50/70 text-emerald-950 font-bold ring-1 ring-emerald-600'
                    : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                }`}
              >
                <Truck className="w-4 h-4 text-emerald-700" />
                <div>
                  <p className="leading-none">Direct to Cycle</p>
                  <p className="text-[10px] text-slate-500 font-normal mt-0.5">Specific Batch Allocation</p>
                </div>
              </button>
            </div>
          </div>

          {/* Cycle Selector if Direct */}
          {isDirectToCycle && (
            <div className="space-y-1">
              <label className="text-xs font-bold text-slate-700">
                Destination Production Cycle *
              </label>
              {cycleCode ? (
                <div className="p-2.5 rounded-md bg-slate-100 border border-slate-200 text-xs font-mono font-bold text-slate-800">
                  {cycleCode}
                </div>
              ) : (
                <Combobox
                  options={cycleOptions}
                  value={selectedCycleId}
                  onChange={setSelectedCycleId}
                  placeholder="Select Active Production Cycle..."
                  searchPlaceholder="Search cycle code or grower..."
                  disabled={loading}
                />
              )}
            </div>
          )}

          {/* Delivery Date & Supplier */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs font-bold text-slate-700">Delivery Date *</label>
              <Input
                type="date"
                value={receiptDate}
                onChange={(e) => setReceiptDate(e.target.value)}
                className="h-9 text-xs bg-white"
                required
              />
            </div>

            <div className="space-y-1">
              <label className="text-xs font-bold text-slate-700">Supplier *</label>
              <Combobox
                options={supplierOptions}
                value={selectedSupplierId}
                onChange={setSelectedSupplierId}
                placeholder="Select Feed Supplier..."
                searchPlaceholder="Search supplier..."
                disabled={loading}
              />
            </div>
          </div>

          {/* Farm Bodega Location & Feed Product */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs font-bold text-slate-700">Farm Location / Bodega *</label>
              <Combobox
                options={farmOptions}
                value={selectedFarmId}
                onChange={setSelectedFarmId}
                placeholder="Select Farm Location..."
                searchPlaceholder="Search farm..."
                disabled={loading}
              />
            </div>

            <div className="space-y-1">
              <label className="text-xs font-bold text-slate-700">Feed Product *</label>
              <Combobox
                options={productOptions}
                value={selectedProductId}
                onChange={handleProductChange}
                placeholder="Select Feed Product..."
                searchPlaceholder="Search product..."
                disabled={loading}
              />
            </div>
          </div>

          {/* Quantity Bags & Total Weight kg */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs font-bold text-slate-700">Quantity (Bags) *</label>
              <Input
                type="number"
                step="any"
                inputMode="decimal"
                value={quantityBags}
                onChange={(e) => setQuantityBags(e.target.value === '' ? '' : Number(e.target.value))}
                placeholder="e.g. 50"
                className="h-9 text-xs font-mono bg-white"
                required
              />
            </div>

            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <label className="text-xs font-bold text-slate-700">Total Weight (kg)</label>
                {selectedProduct?.unit_weight_kg && (
                  <span className="text-[10px] text-emerald-700 font-mono">
                    ({selectedProduct.unit_weight_kg} kg/bag)
                  </span>
                )}
              </div>
              <Input
                type="number"
                step="any"
                inputMode="decimal"
                value={totalWeightKg}
                onChange={(e) => setTotalWeightKg(e.target.value === '' ? '' : Number(e.target.value))}
                placeholder={selectedProduct?.unit_weight_kg ? 'Auto-derived' : 'Enter measured kg'}
                className="h-9 text-xs font-mono bg-white"
              />
              {!selectedProduct?.unit_weight_kg && (
                <p className="text-[10px] text-slate-400">
                  Product has no configured bag weight. Enter measured weight or leave blank (N/A).
                </p>
              )}
            </div>
          </div>

          {/* Pricing & Surcharges */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1">
              <label className="text-xs font-bold text-slate-700">Unit Price (₱/bag) *</label>
              <Input
                type="number"
                step="0.01"
                inputMode="decimal"
                value={unitPrice}
                onChange={(e) => setUnitPrice(e.target.value === '' ? '' : Number(e.target.value))}
                placeholder="₱0.00"
                className="h-9 text-xs font-mono bg-white"
                required
              />
            </div>

            <div className="space-y-1">
              <label className="text-xs font-bold text-slate-700">Markup (₱)</label>
              <Input
                type="number"
                step="0.01"
                inputMode="decimal"
                value={feedMarkupAmount}
                onChange={(e) => setFeedMarkupAmount(e.target.value === '' ? '' : Number(e.target.value))}
                placeholder="₱0.00"
                className="h-9 text-xs font-mono bg-white"
              />
            </div>

            <div className="space-y-1">
              <label className="text-xs font-bold text-slate-700">Trucking Fee (₱)</label>
              <Input
                type="number"
                step="0.01"
                inputMode="decimal"
                value={truckingCost}
                onChange={(e) => setTruckingCost(e.target.value === '' ? '' : Number(e.target.value))}
                placeholder="₱0.00"
                className="h-9 text-xs font-mono bg-white"
              />
            </div>
          </div>

          {/* Reference Nos: DR # & Invoice # */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs font-bold text-slate-700">Delivery Receipt # (DR)</label>
              <Input
                type="text"
                value={deliveryReceiptNo}
                onChange={(e) => setDeliveryReceiptNo(e.target.value)}
                placeholder="e.g. DR-882194"
                className="h-9 text-xs font-mono bg-white"
              />
            </div>

            <div className="space-y-1">
              <label className="text-xs font-bold text-slate-700">Supplier Invoice #</label>
              <Input
                type="text"
                value={invoiceNo}
                onChange={(e) => setInvoiceNo(e.target.value)}
                placeholder="e.g. INV-10492"
                className="h-9 text-xs font-mono bg-white"
              />
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-1">
            <label className="text-xs font-bold text-slate-700">Notes / Remarks</label>
            <Input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional delivery notes"
              className="h-9 text-xs bg-white"
            />
          </div>

          {/* Photo / Invoice Attachment */}
          <div className="space-y-1.5 p-3 rounded-lg border border-slate-200 bg-slate-50">
            <label className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
              <Paperclip className="w-3.5 h-3.5 text-slate-500" />
              Supplier Invoice / Delivery Slip Photo
            </label>
            <div className="flex items-center gap-2">
              <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-300 bg-white hover:bg-slate-50 text-xs font-medium text-slate-700 shadow-xs">
                <Camera className="w-3.5 h-3.5 text-slate-600" />
                <span>{attachedFile ? 'Change Photo' : 'Take Photo / Upload Invoice'}</span>
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  capture="environment"
                  onChange={handleFileChange}
                  className="hidden"
                />
              </label>
              {attachedFile && (
                <span className="text-xs font-mono text-emerald-700 truncate max-w-[200px]">
                  ✓ {attachedFile.name}
                </span>
              )}
            </div>
          </div>

          {/* Computed Financial Summary Callout */}
          <div className="p-3 rounded-lg bg-slate-900 text-white flex items-center justify-between font-mono">
            <div>
              <p className="text-[10px] text-slate-400 uppercase tracking-wider">Total Booked Cost</p>
              <p className="text-xs text-slate-300">
                Feed: {formatCurrency(subtotalFeedCost)}
              </p>
            </div>
            <div className="text-right">
              <span className="text-base sm:text-lg font-bold text-emerald-400">
                {formatCurrency(totalCalculatedCost)}
              </span>
            </div>
          </div>

          <DialogFooter className="pt-2 border-t border-slate-200 flex flex-row items-center justify-end gap-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={loading}
              className="bg-emerald-700 hover:bg-emerald-800 text-white font-semibold shadow-xs"
            >
              {loading ? 'Saving Receipt...' : 'Save Feed Receipt'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
